import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, EmptyState, LoadingSkeleton } from '@nearrme/ui-web';
import { isNearrmeAdminRole, listRoles } from '../api';
import { useAdminAuth } from '../useAdminAuth';

interface HelpTopic {
  slug: string;
  title: string;
  route: string;
  permissionCodes: string[];
  summary: string;
  functionality: string[];
  recentChanges: string[];
}

const HELP_TOPICS: HelpTopic[] = [
  {
    slug: 'dashboard',
    title: 'Dashboard',
    route: '/admin',
    permissionCodes: [],
    summary: 'Operational summary for the signed-in scope.',
    functionality: [
      'KPI cards open the matching detail screen.',
      'Messages opens Contact Us follow-up.',
      'Activity opens audit logs.',
      'Nearrme Admin sees network-wide counts; business users see scoped counts.',
    ],
    recentChanges: [
      'Added Messages and Activity widgets.',
      'Made KPI and widget cards navigable.',
    ],
  },
  {
    slug: 'companies',
    title: 'Companies',
    route: '/admin/companies',
    permissionCodes: ['companies.view', 'companies.manage'],
    summary: 'Company profile, landing page identity, users, products, businesses, and stores.',
    functionality: [
      'Rows open company detail records.',
      'Add company opens a wizard: Company, Users, Products, Businesses.',
      'Company code is generated from company name.',
      'Public URL decorator prepares the future company landing page.',
      'Company logo uses the upload-ready image control.',
    ],
    recentChanges: [
      'Converted the flat form into a wizard.',
      'Added public URL decorator.',
      'Added company logo upload field and image requirements.',
      'Made table columns sortable and rows drill down.',
    ],
  },
  {
    slug: 'brands',
    title: 'Brands',
    route: '/admin/brands',
    permissionCodes: ['company.products.view', 'company.products.manage'],
    summary: 'Manufacturer and brand records tied to company catalog.',
    functionality: [
      'Sort by brand, category, company, and status.',
      'Rows open brand detail records.',
      'Brand details show company, category, description, image, and active state.',
    ],
    recentChanges: [
      'Replaced brand cards with sortable table.',
      'Added brand drill-down detail page.',
    ],
  },
  {
    slug: 'products',
    title: 'Products',
    route: '/admin/products',
    permissionCodes: ['company.products.view', 'company.products.manage', 'store.inventory.manage'],
    summary: 'Catalog products, prices, inventory availability, and customer-facing stock comments.',
    functionality: [
      'Sort by product, brand, category, price, stock, business, and ID.',
      'Rows open product detail records.',
      'Availability shows Available or Out of Stock with comments such as item count or check-back date.',
    ],
    recentChanges: [
      'Added product availability status, quantity, and comment.',
      'Added sortable headers and row drill-down.',
    ],
  },
  {
    slug: 'orders',
    title: 'Orders',
    route: '/admin/orders',
    permissionCodes: ['orders.view.all', 'company.orders.view', 'store.orders.view'],
    summary: 'Online, store, pickup, delivery, reserve, customer, warranty, and order value tracking.',
    functionality: [
      'Filter by all, online-only, and store orders.',
      'Sort by order, status, product, store, customer, total, and warranty.',
      'Rows open order detail records.',
      'Order details show status, channel, service method, customer, store, warranty, and value.',
    ],
    recentChanges: [
      'Added order status, order channel, and service method.',
      'Added sortable table headers and drill-down detail page.',
    ],
  },
  {
    slug: 'consumers',
    title: 'Consumers',
    route: '/admin/consumers',
    permissionCodes: ['consumers.view.all'],
    summary: 'Nearrme-owned consumer accounts and their purchase history.',
    functionality: [
      'Sort by consumer, orders, and spend.',
      'Rows open consumer detail records.',
      'Consumer detail links to related orders.',
    ],
    recentChanges: [
      'Added consumer drill-down page.',
      'Made consumer rows navigable.',
    ],
  },
  {
    slug: 'nearrme-team',
    title: 'Nearrme Team',
    route: '/admin/nearrme-team',
    permissionCodes: ['system.users.manage'],
    summary: 'Nearrme platform users and their profile, role, image, and company assignments.',
    functionality: [
      'Add Nearrme users with role and company scope.',
      'Click a row to edit profile fields.',
      'Edit image URL or select a file for the future CDN upload pipeline.',
      'Save role separately or save the full profile edit.',
    ],
    recentChanges: [
      'Added full edit row for Nearrme Team users.',
      'Added image requirements near user image fields.',
    ],
  },
  {
    slug: 'company-team',
    title: 'Company Team',
    route: '/admin/company-team',
    permissionCodes: ['company.users.manage', 'store.users.manage'],
    summary: 'Company and store staff scoped to a company or store.',
    functionality: [
      'Manage owner, admin, manager, store owner, and store manager roles.',
      'Company and store roles come from Nearrme Admin role templates.',
      'Business users only see data in their assigned scope.',
    ],
    recentChanges: [
      'Separated Nearrme Team from Company Team.',
      'Role model now distinguishes Nearrme, company, store, and consumer roles.',
    ],
  },
  {
    slug: 'messages',
    title: 'Messages',
    route: '/admin/contact-messages',
    permissionCodes: ['messages.manage'],
    summary: 'Contact Us submissions from consumers, businesses, stores, and manufacturers.',
    functionality: [
      'Sort messages by name, status, email, and created date.',
      'Rows open message detail records.',
      'Status can be updated directly in the table.',
    ],
    recentChanges: [
      'Replaced message cards with sortable table.',
      'Added message drill-down detail page.',
    ],
  },
  {
    slug: 'login-options',
    title: 'Login Options',
    route: '/admin/setup/login-options',
    permissionCodes: ['system.permissions.manage'],
    summary: 'Enable or disable login methods.',
    functionality: [
      'Configure email/password, email code, phone code, Google, and passkey options.',
      'Email login and email-code login are enabled for current local testing.',
      'SMS is disabled until India DLT provider setup is finalized.',
    ],
    recentChanges: [
      'Added configurable login options.',
      'Added email-code login flow.',
    ],
  },
  {
    slug: 'roles',
    title: 'Roles',
    route: '/admin/setup/roles',
    permissionCodes: ['system.roles.manage', 'company.roles.manage', 'store.roles.manage'],
    summary: 'Role templates for Nearrme, company, store, and consumer profiles.',
    functionality: [
      'Nearrme Admin controls role templates.',
      'Roles carry permissions that control screen access and available help topics.',
      'Company and store users receive only roles allowed for their scope.',
    ],
    recentChanges: [
      'Added Nearrme, company, store, and consumer role separation.',
      'Help topics now tie to role permissions.',
    ],
  },
  {
    slug: 'permissions',
    title: 'Permissions',
    route: '/admin/setup/permissions',
    permissionCodes: ['system.permissions.manage'],
    summary: 'Permission definitions used to control screens, data, and help visibility.',
    functionality: [
      'Permissions map roles to screen access.',
      'Help center filters pages using the signed-in role permission codes.',
      'Permission descriptions include the related screen path.',
    ],
    recentChanges: [
      'Added permission-aware help filtering.',
    ],
  },
  {
    slug: 'activity',
    title: 'Activity',
    route: '/admin/setup/activity',
    permissionCodes: ['activity.view'],
    summary: 'Security and operational audit log.',
    functionality: [
      'Review sign-ins, role use, and portal events.',
      'Dashboard Activity widget opens this page.',
    ],
    recentChanges: [
      'Moved Activity under collapsible Setup.',
      'Dashboard widget now links here.',
    ],
  },
  {
    slug: 'images-cdn',
    title: 'Images and CDN',
    route: '/admin/help/images-cdn',
    permissionCodes: ['companies.manage', 'company.products.manage', 'store.inventory.manage', 'system.users.manage'],
    summary: 'Image upload, resize, variant generation, and secure CDN publishing.',
    functionality: [
      'User profile images require at least 512 x 512 px square source.',
      'Company logos require at least 1024 x 1024 px square or transparent source.',
      'Product images require at least 1600 x 1600 px square source.',
      'The planned pipeline validates, scans, resizes, stores originals, stores variants, and publishes only CDN URLs.',
    ],
    recentChanges: [
      'Added image requirements module.',
      'Added upload-ready controls for company logo and user image.',
      'Added ST-1745 for secure CDN image upload and resize pipeline.',
    ],
  },
  {
    slug: 'testing',
    title: 'Testing Rules',
    route: '/admin/help/testing',
    permissionCodes: [],
    summary: 'Visible smoke testing and quality expectations for the portal.',
    functionality: [
      'UI smoke testing is visible by default.',
      'Headless testing is only used when explicitly requested for that session.',
      'Contact Us, login, navigation, sorting, drill-down, image controls, and responsive layout require visible smoke coverage.',
    ],
    recentChanges: [
      'Contact Us visible smoke coverage added to required checklist.',
    ],
  },
];

function hasTopicAccess(topic: HelpTopic, permissionCodes: Set<string>, isNearrmeAdmin: boolean) {
  if (isNearrmeAdmin) return true;
  if (topic.permissionCodes.length === 0) return true;
  return topic.permissionCodes.some((permission) => permissionCodes.has(permission));
}

export function AdminHelpPage() {
  const { user } = useAdminAuth();
  const { topicSlug } = useParams<{ topicSlug?: string }>();
  const isNearrmeAdmin = isNearrmeAdminRole(user?.role);
  const roles = useQuery({ queryKey: ['admin', 'roles'], queryFn: listRoles });

  const currentRole = useMemo(
    () => (roles.data ?? []).find((role) => role.roleCode === user?.role),
    [roles.data, user?.role]
  );
  const permissionCodes = useMemo(
    () => new Set(currentRole?.permissionCodes ?? []),
    [currentRole?.permissionCodes]
  );
  const visibleTopics = useMemo(
    () => HELP_TOPICS.filter((topic) => hasTopicAccess(topic, permissionCodes, isNearrmeAdmin)),
    [permissionCodes, isNearrmeAdmin]
  );
  const selectedTopic = visibleTopics.find((topic) => topic.slug === topicSlug);

  if (roles.isLoading) {
    return <LoadingSkeleton className="h-48 w-full" />;
  }

  if (topicSlug && !selectedTopic) {
    return <EmptyState title="Help page unavailable" description="Your role does not have permission to view help for that screen." />;
  }

  if (selectedTopic) {
    return (
      <div className="space-y-4">
        <Link to="/admin/help" className="text-sm font-semibold text-brand-700 hover:underline dark:text-brand-300">
          Back to Help
        </Link>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Help page</div>
          <h1 className="mt-1 text-3xl font-bold text-slate-900 dark:text-slate-100">{selectedTopic.title}</h1>
          <p className="mt-1 text-slate-600 dark:text-slate-400">{selectedTopic.summary}</p>
        </div>

        <Card padded className="border border-slate-200 shadow-sm dark:border-slate-800">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <h2 className="text-lg font-semibold text-slate-950 dark:text-slate-50">Functionality</h2>
              <HelpList items={selectedTopic.functionality} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-950 dark:text-slate-50">Recent changes</h2>
              <HelpList items={selectedTopic.recentChanges} />
            </div>
          </div>
          <div className="mt-5 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:bg-slate-950 dark:text-slate-400">
            Screen: <span className="font-mono">{selectedTopic.route}</span>
            {selectedTopic.permissionCodes.length > 0 && (
              <> | Permissions: <span className="font-mono">{selectedTopic.permissionCodes.join(', ')}</span></>
            )}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Help</h1>
        <p className="mt-1 text-slate-600 dark:text-slate-400">
          Screen-by-screen guide for the Nearrme portal. Help pages are filtered by your role permissions.
        </p>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        {visibleTopics.map((topic) => (
          <Link key={topic.slug} to={`/admin/help/${topic.slug}`} className="block rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500">
            <Card padded className="h-full border border-slate-200 shadow-sm transition hover:border-brand-300 hover:shadow-md dark:border-slate-800 dark:hover:border-brand-700">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-slate-950 dark:text-slate-50">{topic.title}</h2>
                  <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">{topic.summary}</p>
                </div>
                <span className="rounded-full bg-brand-50 px-2 py-1 text-xs font-semibold text-brand-700 dark:bg-brand-950 dark:text-brand-300">
                  Open
                </span>
              </div>
              <div className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                {topic.permissionCodes.length ? topic.permissionCodes.join(', ') : 'Available to all portal users'}
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}

function HelpList({ items }: { items: string[] }) {
  return (
    <ul className="mt-3 space-y-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
      {items.map((item) => (
        <li key={item} className="flex gap-2">
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-600" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}
